import { useTransition } from "@synthesis/shared-services/internal/application/hooks";
import { Button, Modal, setPromiseSnack, TextArea } from "@synthesis/shared-ui";
import { useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Uploader } from "../../../components/uploader";
import { useUserInfo } from "../../../shared/hooks/use-user-info";
import { invalidateInternalApplicationQueries } from "../../../shared/utils/invalidate-application-queries";
import { usePageStore } from "../application-detail-page.lib";
import type { RejectApplicationPopupAction } from "./application-control-popup.lib";
import {
  REJECT_MODAL_HEADING,
  REJECT_MODAL_TITLE,
  REJECT_REASON_LABEL,
  REJECT_REASON_PLACEHOLDER,
  REJECT_SNACK_DESCRIPTIONS,
  REJECT_SNACK_MESSAGES,
  uploadRejectDocuments,
} from "./application-control-reject-modal.lib";

type RejectApplicationFormValues = {
  reason: string;
};

const RejectApplicationModal = observer(({ popupAction }: { popupAction: RejectApplicationPopupAction }) => {
  const store = usePageStore();
  const queryClient = useQueryClient();
  const userInfo = useUserInfo();
  const transitionMutation = useTransition();
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [uploaderMountKey, setUploaderMountKey] = useState(0);

  const methods = useForm<RejectApplicationFormValues>({
    defaultValues: { reason: "" },
    mode: "onChange",
  });

  const reason = methods.watch("reason");
  const isPending = transitionMutation.isPending;
  const isSubmitDisabled = isPending || !reason.trim();

  useEffect(() => {
    methods.reset({ reason: "" });
    setAttachedFiles([]);
    setUploaderMountKey((key) => key + 1);
  }, [methods, popupAction]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      store?.setPopupAction(null);
    }
  };

  const handleSelectFiles = (files: File[]) => {
    setAttachedFiles(files);
  };

  const handleSubmit = methods.handleSubmit(async (formData) => {
    if (!userInfo) {
      return;
    }

    const promise = (async () => {
      const documentIds = await uploadRejectDocuments({
        requestId: popupAction.requestId,
        files: attachedFiles,
        userId: userInfo.id,
      });

      await transitionMutation.mutateAsync({
        path: { requestId: popupAction.requestId, action: "reject" },
        body: {
          comment: formData.reason.trim(),
          documentIds,
        },
      });

      await invalidateInternalApplicationQueries(queryClient, popupAction.requestId);
      store?.setPopupAction(null);
    })();

    setPromiseSnack({
      promise,
      promiseSettings: {
        title: REJECT_SNACK_MESSAGES,
        description: REJECT_SNACK_DESCRIPTIONS,
      },
    });
  });

  return (
    <Modal.Root isOpen setIsOpen={handleOpenChange}>
      <Modal.Content className="flex max-h-[calc(100vh-4rem)] w-[700px] flex-col overflow-hidden">
        <Modal.Title>{REJECT_MODAL_TITLE}</Modal.Title>
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-6 p-8">
              <h3 className="heading-h4 text-type-common">{REJECT_MODAL_HEADING}</h3>

              <FormProvider {...methods}>
                <div>
                  <h4 className="subtitle-lg text-type-common after:text-type-error mb-3 after:ml-1 after:content-['*']">{REJECT_REASON_LABEL}</h4>
                  <TextArea name="reason" rows={5} placeholder={REJECT_REASON_PLACEHOLDER} disabled={isPending} />
                </div>
              </FormProvider>

              <div>
                <Uploader key={uploaderMountKey} multiple isViewFileList allowDeleteFromList onSelectFiles={handleSelectFiles} />
              </div>
            </div>
          </div>

          <div className="flex shrink-0 gap-4 px-8 pb-6 pt-2">
            <Button type="submit" theme="primary" size="lg" disabled={isSubmitDisabled}>
              Подтвердить
            </Button>
            <Button type="button" theme="secondary" size="lg" disabled={isPending} onClick={() => handleOpenChange(false)}>
              Отмена
            </Button>
          </div>
        </form>
      </Modal.Content>
    </Modal.Root>
  );
});

export const ApplicationControlRejectModal = observer(() => {
  const store = usePageStore();
  const popupAction = store?.popupAction;

  if (!popupAction || popupAction.type !== "reject-application") {
    return null;
  }

  return <RejectApplicationModal popupAction={popupAction} />;
});
